import { useState, useEffect, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X, HelpCircle, Pencil, CalendarIcon, MapPin, UserPlus, Check, ChevronLeft, Palette, Gift, CreditCard, Users, Trash2, FileText, Sparkles, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const FabricEditor = lazy(() => import("@/components/admin/FabricEditor"));
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { updateCollection, deleteCollection } from "@/services/collections";
import { getGiftDataByRecipientEmail } from "@/services/wishlists";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { Tables } from "@/integrations/supabase/types";
import { getActiveTemplateColumns } from "@/services/templates";
import { useCurrency } from "@/hooks/useCurrency";
import GiftGuidanceWizard from "@/components/card/GiftGuidanceWizard";
import MusicPicker from "@/components/card/MusicPicker";
import RsvpQuestionBuilder, { type RsvpQuestion } from "@/components/card/RsvpQuestionBuilder";


interface Props {
  collection: Tables<"collections">;
  onClose: () => void;
  onUpdate: (updates: Partial<Tables<"collections">>) => void;
  initialSection?: "home" | "details" | "design" | "gifts" | "chipin" | "rsvp" | "danger";
}

const SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const RESERVED_SLUGS = ["auth", "dashboard", "create", "cards", "api", "admin", "settings"];

function validateSlug(slug: string, t: (key: string) => string): string | null {
  if (!slug || slug.length < 3) return t("settings.slugMinLength");
  if (slug.length > 60) return t("settings.slugMaxLength");
  if (!SLUG_REGEX.test(slug)) return t("settings.slugInvalidChars");
  if (RESERVED_SLUGS.includes(slug)) return t("settings.slugReserved");
  return null;
}

const SettingsModal = ({ collection, onClose, onUpdate, initialSection = "home" }: Props) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currency = useCurrency();
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const col = collection as any;

  // ── Card Details ──
  const [cardName, setCardName] = useState(collection.title);
  const [recipientName, setRecipientName] = useState(collection.recipient_name);
  const [recipientEmail, setRecipientEmail] = useState(collection.recipient_email || "");
  const [editingSlug, setEditingSlug] = useState(false);
  const [slug, setSlug] = useState(collection.share_slug || "");
  const [slugError, setSlugError] = useState<string | null>(null);
  // Product pools merge the "Message to group" field into Details so
  // organisers configure the whole pool from one screen. Other pool
  // types keep their standalone message modal.
  const isProductPool = (col.pool_type as string | undefined) === "product";
  const [messageToGroup, setMessageToGroup] = useState(collection.message_to_group || "");

  // ── Design ──
  const [selectedTemplateId, setSelectedTemplateId] = useState(collection.card_template_id || "");
  const [templates, setTemplates] = useState<{ id: string; name: string; image_url: string; occasion: string; price: number | null }[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [envelopeTint, setEnvelopeTint] = useState((col.envelope_tint as string) || "");
  const [selectedMusicId, setSelectedMusicId] = useState<string | null>((col.music_id as string) || null);
  const [designTab, setDesignTab] = useState<"card" | "envelope" | "music">("card");
  // ── Customizer ──
  const [customCoverUrl, setCustomCoverUrl] = useState<string>((col.custom_cover_url as string) || "");
  const [customDesignJson, setCustomDesignJson] = useState<Record<string, unknown> | null>(
    (col.card_design_json as Record<string, unknown> | null) || null
  );
  const [customizerOpen, setCustomizerOpen] = useState(false);
  const [customizerJson, setCustomizerJson] = useState<Record<string, unknown> | null>(null);
  const [loadingTemplateJson, setLoadingTemplateJson] = useState(false);

  const handleOpenCustomizer = async () => {
    if (!selectedTemplateId) return;
    setLoadingTemplateJson(true);
    try {
      // If the selected template still matches the saved one AND the user
      // has a prior customization, resume from it. Otherwise pull a fresh
      // copy of the template to start from. Either way the user's edits go
      // back to `collections.card_design_json` and never touch the template.
      const sameTemplate = selectedTemplateId === (collection.card_template_id || "");
      if (sameTemplate && customDesignJson) {
        setCustomizerJson(customDesignJson);
      } else {
        const { data: tmpl, error } = await supabase
          .from("card_templates")
          .select("polotno_json")
          .eq("id", selectedTemplateId)
          .maybeSingle();
        if (error) throw error;
        if (!tmpl?.polotno_json) {
          toast({
            title: t("settings.notCustomizable", { defaultValue: "Not customizable" }),
            description: t("settings.notCustomizableDesc", {
              defaultValue: "This template does not have an editable design yet.",
            }),
          });
          return;
        }
        setCustomizerJson(tmpl.polotno_json as Record<string, unknown>);
      }
      setCustomizerOpen(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to load template";
      toast({ variant: "destructive", title: "Error", description: message });
    } finally {
      setLoadingTemplateJson(false);
    }
  };

  const handleCustomizerSave = ({
    coverUrl,
    designJson,
  }: {
    coverUrl: string;
    designJson: Record<string, unknown>;
  }) => {
    setCustomCoverUrl(coverUrl);
    setCustomDesignJson(designJson);
    setCustomizerOpen(false);
    toast({ title: t("settings.cardUpdated", { defaultValue: "Card updated" }) });
  };

  useEffect(() => {
    getActiveTemplateColumns().then(({ data }) => {
      setTemplates(data);
      setLoadingTemplates(false);
    });
  }, []);

  // ── Require Login ──
  const [requireLogin, setRequireLogin] = useState(col.require_login ?? false);

  // ── Money Collection ──
  const [addGroupCard, setAddGroupCard] = useState(collection.collection_enabled ?? true);
  const [contributionType, setContributionType] = useState(collection.contribution_type || "any_amount");
  const [setAmount, setSetAmount] = useState(collection.set_amount || 0);
  const [collectionGoal, setCollectionGoal] = useState(col.collection_goal ?? false);
  const [goalAmount, setGoalAmount] = useState(col.collection_goal_amount ?? "");
  const [showTotalCollected, setShowTotalCollected] = useState(col.show_total_collected ?? false);
  const [signWithoutContributing, setSignWithoutContributing] = useState(col.sign_without_contributing ?? true);
  const [collectionNote, setCollectionNote] = useState(col.collection_note || "");

  // ── RSVP ──
  const [rsvpEnabled, setRsvpEnabled] = useState(!!col.rsvp_enabled);
  const [rsvpLocationName, setRsvpLocationName] = useState(col.rsvp_location_name || "");
  const [rsvpLocationAddress, setRsvpLocationAddress] = useState(col.rsvp_location_address || "");
  const [rsvpEventDate, setRsvpEventDate] = useState<Date | undefined>(
    col.rsvp_event_date ? new Date(col.rsvp_event_date) : undefined
  );
  const [rsvpEventTime, setRsvpEventTime] = useState(
    col.rsvp_event_date ? format(new Date(col.rsvp_event_date), "HH:mm") : "18:00"
  );
  const [rsvpPlusOne, setRsvpPlusOne] = useState(!!col.rsvp_plus_one);
  const [rsvpQuestions, setRsvpQuestions] = useState<RsvpQuestion[]>(
    Array.isArray(col.rsvp_questions) ? col.rsvp_questions : []
  );

  // ── Invite-Only & Capacity ──
  const [inviteOnly, setInviteOnly] = useState(!!col.invite_only);
  const [maxCapacity, setMaxCapacity] = useState<string>(col.max_capacity ? String(col.max_capacity) : "");

  // ── Scheduling ──
  const existingDeadline = collection.deadline ? new Date(collection.deadline) : undefined;
  const [deadline, setDeadline] = useState<Date | undefined>(existingDeadline);
  const [deadlineTime, setDeadlineTime] = useState(() => {
    if (!existingDeadline) return "05:00";
    let h = existingDeadline.getHours();
    if (h === 0) h = 12;
    else if (h > 12) h -= 12;
    return `${String(h).padStart(2, "0")}:00`;
  });
  const [deadlinePeriod, setDeadlinePeriod] = useState<"AM" | "PM">(() => {
    if (!existingDeadline) return "PM";
    return existingDeadline.getHours() >= 12 ? "PM" : "AM";
  });
  const [timezone, setTimezone] = useState(collection.timezone || "America/Denver");
  const [autoReminders, setAutoReminders] = useState(col.auto_reminders ?? true);

  const handleSlugChange = (value: string) => {
    const sanitized = value.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 60);
    setSlug(sanitized);
    setSlugError(validateSlug(sanitized, t));
  };

  const buildDeadlineISO = (): string | null => {
    if (!deadline) return null;
    const date = new Date(deadline);
    const [hourStr] = deadlineTime.split(":");
    let hour = parseInt(hourStr, 10);
    if (deadlinePeriod === "PM" && hour < 12) hour += 12;
    if (deadlinePeriod === "AM" && hour === 12) hour = 0;
    date.setHours(hour, 0, 0, 0);
    return date.toISOString();
  };

  const buildRsvpDateISO = (): string | null => {
    if (!rsvpEventDate) return null;
    const dt = new Date(rsvpEventDate);
    const [h, m] = rsvpEventTime.split(":").map(Number);
    dt.setHours(h, m, 0, 0);
    return dt.toISOString();
  };

  const handleSave = async () => {
    const slugValidationError = validateSlug(slug, t);
    if (slugValidationError) {
      setSlugError(slugValidationError);
      toast({ variant: "destructive", title: t("settings.invalidUrl"), description: slugValidationError });
      return;
    }
    if (rsvpEnabled && !rsvpLocationName.trim()) {
      toast({ variant: "destructive", title: t("settings.missingVenue"), description: t("settings.missingVenueDesc") });
      return;
    }

    // Check if switching to a paid template
    const templateChanged = selectedTemplateId !== (collection.card_template_id || "");
    const selectedTmpl = templates.find(t => t.id === selectedTemplateId);
    const switchingToPaid = templateChanged && selectedTmpl && selectedTmpl.price != null && selectedTmpl.price > 0;

    if (switchingToPaid) {
      // Save all other changes first (without template change)
      setSaving(true);
      const otherUpdates: Record<string, unknown> = {
        title: cardName,
        recipient_name: recipientName,
        recipient_email: recipientEmail.trim() || null,
        share_slug: slug,
        envelope_tint: envelopeTint || null,
        music_id: selectedMusicId || null,
        contribution_type: contributionType,
        set_amount: contributionType === "set_amount" ? setAmount : null,
        collection_enabled: addGroupCard,
        collection_goal: collectionGoal,
        collection_goal_amount: collectionGoal && goalAmount ? Number(goalAmount) : null,
        auto_reminders: autoReminders,
        show_total_collected: showTotalCollected,
        sign_without_contributing: signWithoutContributing,
        collection_note: addGroupCard ? collectionNote.trim() || null : null,
        deadline: buildDeadlineISO(),
        timezone,
        rsvp_enabled: rsvpEnabled,
        rsvp_location_name: rsvpEnabled ? rsvpLocationName.trim() : null,
        rsvp_location_address: rsvpEnabled ? rsvpLocationAddress.trim() || null : null,
        rsvp_event_date: rsvpEnabled ? buildRsvpDateISO() : null,
        rsvp_plus_one: rsvpEnabled ? rsvpPlusOne : false,
        require_login: requireLogin,
        rsvp_questions: rsvpEnabled ? rsvpQuestions.filter(q => q.question.trim()) : [],
        invite_only: rsvpEnabled ? inviteOnly : false,
        max_capacity: rsvpEnabled && maxCapacity ? Number(maxCapacity) : null,
        ...(isProductPool ? { message_to_group: messageToGroup.trim() || null } : {}),
      };
      await updateCollection(collection.id, otherUpdates);
      onUpdate(otherUpdates as Partial<Tables<"collections">>);
      setSaving(false);
      onClose();
      // Navigate to checkout page with template info
      navigate(`/card/${collection.share_slug}/checkout`, {
        state: {
          templateId: selectedTemplateId,
          templateName: selectedTmpl.name,
          templateUrl: selectedTmpl.image_url,
          templatePrice: selectedTmpl.price,
          collectionId: collection.id,
          occasion: collection.occasion,
          recipientName: cardName || collection.recipient_name,
        },
      });
      return;
    }

    setSaving(true);
    // If the user switched to a different template, drop any customization
    // that belonged to the previous template.
    const templateSwitched = selectedTemplateId !== (collection.card_template_id || "");
    const resolvedCoverUrl = templateSwitched ? null : (customCoverUrl || null);
    const resolvedDesignJson = templateSwitched ? null : customDesignJson;
    const updates: Record<string, unknown> = {
      title: cardName,
      recipient_name: recipientName,
      recipient_email: recipientEmail.trim() || null,
      share_slug: slug,
      card_template_id: selectedTemplateId || null,
      custom_cover_url: resolvedCoverUrl,
      card_design_json: resolvedDesignJson,
      envelope_tint: envelopeTint || null,
      music_id: selectedMusicId || null,
      contribution_type: contributionType,
      set_amount: contributionType === "set_amount" ? setAmount : null,
      collection_enabled: addGroupCard,
      collection_goal: collectionGoal,
      collection_goal_amount: collectionGoal && goalAmount ? Number(goalAmount) : null,
      auto_reminders: autoReminders,
      show_total_collected: showTotalCollected,
      sign_without_contributing: signWithoutContributing,
      collection_note: addGroupCard ? collectionNote.trim() || null : null,
      deadline: buildDeadlineISO(),
      timezone,
      rsvp_enabled: rsvpEnabled,
      rsvp_location_name: rsvpEnabled ? rsvpLocationName.trim() : null,
      rsvp_location_address: rsvpEnabled ? rsvpLocationAddress.trim() || null : null,
      rsvp_event_date: rsvpEnabled ? buildRsvpDateISO() : null,
      rsvp_plus_one: rsvpEnabled ? rsvpPlusOne : false,
      require_login: requireLogin,
      rsvp_questions: rsvpEnabled ? rsvpQuestions.filter(q => q.question.trim()) : [],
      invite_only: rsvpEnabled ? inviteOnly : false,
      max_capacity: rsvpEnabled && maxCapacity ? Number(maxCapacity) : null,
      ...(isProductPool ? { message_to_group: messageToGroup.trim() || null } : {}),
    };

    const { error } = await updateCollection(collection.id, updates);

    if (error) {
      const message = error.message || t("settings.failedToSave");
      if (message.includes("unique") || message.includes("duplicate") || message.includes("share_slug")) {
        setSlugError(t("settings.urlAlreadyTaken"));
        toast({ variant: "destructive", title: t("settings.urlTaken"), description: t("settings.urlTakenDesc") });
      } else {
        toast({ variant: "destructive", title: t("common.error"), description: message });
      }
    } else {
      // If recipient email changed, re-fetch their profile & preferences
      const emailChanged = recipientEmail.trim().toLowerCase() !== (collection.recipient_email || "").trim().toLowerCase();
      if (emailChanged && recipientEmail.trim()) {
        const { data: giftData } = await getGiftDataByRecipientEmail(recipientEmail.trim());
        if (giftData?.profile) {
          const prefUpdates: Record<string, unknown> = {
            preferences_wants: giftData.profile.preferences_wants || null,
            preferences_has: giftData.profile.preferences_has || null,
            preferences_style: giftData.profile.preferences_style || null,
            preferences_registries: giftData.profile.preferences_registries || [],
          };
          await updateCollection(collection.id, prefUpdates);
          Object.assign(updates, prefUpdates);
        }
      }
      onUpdate(updates as Partial<Tables<"collections">>);
      toast({ title: t("settings.settingsSaved"), description: t("settings.settingsSavedDesc") });
      onClose();
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setDeleting(true);
    const { error } = await deleteCollection(collection.id);
    if (error) {
      console.error("Delete collection error:", error);
      toast({ variant: "destructive", title: t("common.error"), description: error.message });
    } else {
      toast({ title: t("settings.deleted"), description: t("settings.deletedDesc") });
      onClose();
      navigate("/dashboard");
    }
    setDeleting(false);
    setConfirmDelete(false);
  };

  type Section = "home" | "details" | "design" | "gifts" | "chipin" | "rsvp" | "danger";
  const [activeSection, setActiveSection] = useState<Section>(initialSection);

  const sections: { key: Section; icon: typeof Palette; label: string; desc: string }[] = [
    { key: "details", icon: FileText, label: t("settings.details"), desc: t("settings.detailsDesc") },
    { key: "design", icon: Palette, label: t("settings.design"), desc: t("settings.designDesc") },
    { key: "gifts", icon: Gift, label: t("settings.giftGuidance"), desc: t("settings.giftGuidanceDesc") },
    { key: "chipin", icon: CreditCard, label: t("settings.chipIn"), desc: t("settings.chipInDesc") },
    { key: "rsvp", icon: Users, label: t("settings.rsvp"), desc: t("settings.rsvpDesc") },
    { key: "danger", icon: Trash2, label: t("settings.dangerZone"), desc: t("settings.dangerZoneDesc") },
  ];

  const renderSectionContent = () => {
    switch (activeSection) {
      case "details":
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">{t("settings.cardName")}</Label>
              <Input value={cardName} onChange={(e) => setCardName(e.target.value)} className="border-border" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">{t("settings.recipientName") || "Recipient Name"}</Label>
              <Input value={recipientName} onChange={(e) => setRecipientName(e.target.value)} className="border-border" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">{t("settings.recipientEmail") || "Recipient Email"}</Label>
              <Input type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} placeholder="recipient@example.com" className="border-border" />
              <p className="text-[10px] text-muted-foreground mt-1">{t("settings.recipientEmailHint") || "The card will be delivered to this email"}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mt-1.5">{window.location.origin}/card/{slug}</p>
              {editingSlug ? (
                <div className="flex flex-col gap-1 mt-1">
                  <div className="flex items-center gap-2">
                    <Input value={slug} onChange={(e) => handleSlugChange(e.target.value)} className={cn("border-border text-xs h-8", slugError && "border-destructive")} />
                    <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setEditingSlug(false)}>{t("settings.done")}</Button>
                  </div>
                  {slugError && <p className="text-xs text-destructive">{slugError}</p>}
                </div>
              ) : (
                <button onClick={() => setEditingSlug(true)} className="flex items-center gap-1 text-xs text-primary hover:underline mt-1">
                  <Pencil size={12} /> {t("settings.editUrl")}
                </button>
              )}
            </div>
            {isProductPool && (
              <div className="border-t border-border pt-4 space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-muted-foreground block">
                    {t("settings.messageToGroupTitle", { defaultValue: "Message to group" })}
                  </Label>
                  <span className="text-[10px] text-muted-foreground tabular-nums">{messageToGroup.length}/500</span>
                </div>
                <Textarea
                  value={messageToGroup}
                  onChange={(e) => setMessageToGroup(e.target.value.slice(0, 500))}
                  placeholder={t("settings.messageToGroupPlaceholder", {
                    defaultValue: `Let's surprise ${recipientName || "them"} with something special!`,
                    name: recipientName || "them",
                  })}
                  className="min-h-[120px] resize-none"
                  rows={5}
                />
                <p className="text-[10px] text-muted-foreground">
                  {t("settings.messageToGroupDesc", {
                    defaultValue: "Write a personal message your group will see when they receive the invitation.",
                  })}
                </p>
              </div>
            )}
            <div className="border-t border-border pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm text-foreground">{t("settings.requireLogin")}</span>
                  <p className="text-xs text-muted-foreground mt-0.5">{t("settings.requireLoginDesc")}</p>
                </div>
                <Switch checked={requireLogin} onCheckedChange={setRequireLogin} />
              </div>
            </div>
          </div>
        );

      case "design": {
        const ENVELOPE_TINT_OPTIONS = [
          { label: "Classic", value: "", color: "#d4b896" },
          { label: "Blush", value: "hue-rotate(330deg) saturate(1.6) brightness(0.92)", color: "#c4707a" },
          { label: "Rose", value: "hue-rotate(340deg) saturate(2) brightness(0.85)", color: "#a8505a" },
          { label: "Wine", value: "hue-rotate(330deg) saturate(2.2) brightness(0.7)", color: "#7a2e3a" },
          { label: "Navy", value: "hue-rotate(200deg) saturate(2) brightness(0.65)", color: "#2c4a6e" },
          { label: "Ocean", value: "hue-rotate(190deg) saturate(1.8) brightness(0.78)", color: "#3a6e8a" },
          { label: "Emerald", value: "hue-rotate(120deg) saturate(1.8) brightness(0.72)", color: "#2d6b4a" },
          { label: "Forest", value: "hue-rotate(100deg) saturate(2) brightness(0.6)", color: "#1e4a2c" },
          { label: "Plum", value: "hue-rotate(260deg) saturate(1.8) brightness(0.7)", color: "#5a2d6e" },
          { label: "Charcoal", value: "saturate(0.2) brightness(0.55)", color: "#4a4a4a" },
          { label: "Mocha", value: "hue-rotate(20deg) saturate(1.5) brightness(0.7)", color: "#6e4a2d" },
          { label: "Burgundy", value: "hue-rotate(350deg) saturate(2.5) brightness(0.6)", color: "#6b1a2a" },
          { label: "Slate", value: "hue-rotate(210deg) saturate(0.8) brightness(0.65)", color: "#4a5568" },
          { label: "Gold", value: "hue-rotate(35deg) saturate(2) brightness(0.85)", color: "#b8860b" },
          { label: "Olive", value: "hue-rotate(60deg) saturate(1.5) brightness(0.7)", color: "#5a6e2d" },
        ];
        return (
          <div className="space-y-5">
            {/* Tab bar — matches Dashboard "My Cards / Participating" style */}
            <div className="flex gap-1 bg-muted/50 rounded-full p-1 w-fit">
              <button
                onClick={() => setDesignTab("card")}
                className={cn(
                  "px-4 py-1.5 rounded-full text-xs font-medium transition-all",
                  designTab === "card"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {t("settings.designTab.card", { defaultValue: "Card Design" })}
              </button>
              <button
                onClick={() => setDesignTab("envelope")}
                className={cn(
                  "px-4 py-1.5 rounded-full text-xs font-medium transition-all",
                  designTab === "envelope"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {t("settings.designTab.envelope", { defaultValue: "Envelope Color" })}
              </button>
              <button
                onClick={() => setDesignTab("music")}
                className={cn(
                  "px-4 py-1.5 rounded-full text-xs font-medium transition-all",
                  designTab === "music"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {t("settings.designTab.music", { defaultValue: "Background Music" })}
              </button>
            </div>

            {/* Card Design */}
            {designTab === "card" && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">{t("settings.chooseDesign")}</p>
                {loadingTemplates ? (
                  <div className="grid grid-cols-3 gap-2">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="aspect-[3/4] rounded-lg bg-muted animate-pulse" />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {templates.map((tmpl) => {
                      const isFree = !tmpl.price || tmpl.price <= 0;
                      const sameTemplate = selectedTemplateId === (collection.card_template_id || "");
                      const thumb = selectedTemplateId === tmpl.id && sameTemplate && customCoverUrl
                        ? customCoverUrl
                        : tmpl.image_url;
                      return (
                        <button
                          key={tmpl.id}
                          onClick={() => setSelectedTemplateId(tmpl.id)}
                          className={cn(
                            "relative aspect-[3/4] rounded-lg overflow-hidden border-2 transition-all",
                            selectedTemplateId === tmpl.id ? "border-primary shadow-md" : "border-transparent hover:border-border"
                          )}
                        >
                          <img src={thumb} alt={tmpl.name} className="w-full h-full object-cover" />
                          <div className="absolute top-1.5 start-1.5 z-10">
                            <span className={`text-[8px] font-bold uppercase px-1.5 py-0.5 rounded-full ${
                              isFree
                                ? "bg-emerald-500/90 text-white"
                                : "bg-amber-500/90 text-white"
                            }`}>
                              {isFree ? t("wizard.design.free", { defaultValue: "Free" }) : `${currency} ${tmpl.price}`}
                            </span>
                          </div>
                          {selectedTemplateId === tmpl.id && (
                            <div className="absolute top-1.5 end-1.5 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                              <Check size={12} className="text-primary-foreground" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Customize the selected template */}
                {selectedTemplateId && (
                  <button
                    onClick={handleOpenCustomizer}
                    disabled={loadingTemplateJson}
                    className="w-full flex items-center justify-center gap-2 rounded-full border border-primary/40 bg-primary/5 hover:bg-primary/10 text-primary text-sm font-medium py-3 transition-colors disabled:opacity-60"
                  >
                    {loadingTemplateJson ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Sparkles size={16} />
                    )}
                    {customDesignJson && selectedTemplateId === (collection.card_template_id || "")
                      ? t("wizard.design.reEdit", { defaultValue: "Re-edit this card" })
                      : t("wizard.design.customize", { defaultValue: "Customize this card" })}
                  </button>
                )}
              </div>
            )}

            {/* Envelope Color */}
            {designTab === "envelope" && (
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium text-foreground">{t("settings.envelopeTint", { defaultValue: "Envelope Color" })}</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">{t("settings.envelopeTintDesc", { defaultValue: "Choose a color for the envelope" })}</p>
                </div>
                <div className="grid grid-cols-5 gap-2">
                  {ENVELOPE_TINT_OPTIONS.map((opt) => (
                    <button
                      key={opt.label}
                      onClick={() => setEnvelopeTint(opt.value)}
                      className={cn(
                        "flex flex-col items-center gap-1 p-1.5 rounded-lg border-2 transition-all",
                        envelopeTint === opt.value ? "border-primary" : "border-transparent hover:border-border"
                      )}
                      title={opt.label}
                    >
                      <div
                        className="w-9 h-9 rounded-full shadow-sm relative"
                        style={{ backgroundColor: opt.color }}
                      >
                        {envelopeTint === opt.value && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Check size={14} className="text-white drop-shadow-md" />
                          </div>
                        )}
                      </div>
                      <span className="text-[9px] text-muted-foreground leading-tight">{opt.label}</span>
                    </button>
                  ))}
                </div>
                {/* Custom color input */}
                <div className="flex items-center gap-2 pt-1">
                  <label className="text-xs text-muted-foreground">Custom:</label>
                  <input
                    type="color"
                    className="w-8 h-8 rounded-full border border-border cursor-pointer p-0 bg-transparent"
                    onChange={(e) => {
                      // Convert hex to hue-rotate: extract hue from the color
                      const hex = e.target.value;
                      const r = parseInt(hex.slice(1, 3), 16) / 255;
                      const g = parseInt(hex.slice(3, 5), 16) / 255;
                      const b = parseInt(hex.slice(5, 7), 16) / 255;
                      const max = Math.max(r, g, b), min = Math.min(r, g, b);
                      const l = (max + min) / 2;
                      let h = 0;
                      if (max !== min) {
                        const d = max - min;
                        if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
                        else if (max === g) h = ((b - r) / d + 2) * 60;
                        else h = ((r - g) / d + 4) * 60;
                      }
                      // Base envelope hue is ~30deg (warm tan), so offset
                      const hueShift = Math.round(h - 30);
                      const sat = Math.max(0.8, 1 + (1 - l) * 1.5);
                      const bright = Math.max(0.5, Math.min(0.95, l + 0.1));
                      setEnvelopeTint(`hue-rotate(${hueShift}deg) saturate(${sat.toFixed(1)}) brightness(${bright.toFixed(2)})`);
                    }}
                  />
                  {envelopeTint && !ENVELOPE_TINT_OPTIONS.some(o => o.value === envelopeTint) && (
                    <span className="text-[10px] text-primary font-medium">Custom color active</span>
                  )}
                </div>
              </div>
            )}

            {/* Background Music */}
            {designTab === "music" && (
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium text-foreground">{t("settings.designTab.music", { defaultValue: "Background Music" })}</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">{t("settings.musicDesc", { defaultValue: "Pick a track that plays when the card is opened" })}</p>
                </div>
                <MusicPicker
                  occasion={collection.occasion}
                  selectedMusicId={selectedMusicId}
                  onSelect={setSelectedMusicId}
                />
              </div>
            )}
          </div>
        );
      }

      case "gifts":
        return (
          <GiftGuidanceWizard
            collection={collection}
            embedded
            onClose={() => setActiveSection("home")}
            onUpdate={(updates) => {
              onUpdate(updates);
              setActiveSection("home");
            }}
          />
        );

      case "chipin":
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground">{t("settings.enableChipIn")}</span>
              <Switch checked={addGroupCard} onCheckedChange={setAddGroupCard} />
            </div>
            {addGroupCard && (
              <div className="space-y-4 ps-1">
                <div>
                  <Label className="text-xs text-muted-foreground mb-2 block">{t("settings.contributionType")}</Label>
                  <div className="flex gap-2">
                    <button onClick={() => setContributionType("any_amount")} className={cn("flex-1 text-sm py-2 rounded-lg border transition-colors", contributionType === "any_amount" ? "border-primary bg-primary/5 text-primary font-medium" : "border-border text-foreground hover:border-primary/30")}>{t("settings.anyAmount")}</button>
                    <button onClick={() => setContributionType("set_amount")} className={cn("flex-1 text-sm py-2 rounded-lg border transition-colors", contributionType === "set_amount" ? "border-primary bg-primary/5 text-primary font-medium" : "border-border text-foreground hover:border-primary/30")}>{t("settings.fixedAmount")}</button>
                  </div>
                  {contributionType === "set_amount" && (
                    <Input type="number" value={setAmount} onChange={(e) => setSetAmount(Number(e.target.value))} placeholder={t("settings.amountPlaceholder")} className="border-border mt-2 h-9 text-sm" />
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground">{t("settings.collectionGoal")}</span>
                  <Switch checked={collectionGoal} onCheckedChange={setCollectionGoal} />
                </div>
                {collectionGoal && (
                  <Input type="number" min="1" value={goalAmount} onChange={(e) => setGoalAmount(e.target.value)} placeholder={t("settings.targetAmount")} className="border-border h-9 text-sm" />
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm text-foreground">{t("settings.showTotalCollected")}</span>
                    <HelpCircle size={13} className="text-muted-foreground" />
                  </div>
                  <Switch checked={showTotalCollected} onCheckedChange={setShowTotalCollected} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground">{t("settings.signWithoutContributing")}</span>
                  <Switch checked={signWithoutContributing} onCheckedChange={setSignWithoutContributing} />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1.5 block">{t("settings.fundsUsedFor")}</Label>
                  <textarea
                    value={collectionNote}
                    onChange={(e) => setCollectionNote(e.target.value)}
                    placeholder={t("settings.fundsPlaceholder")}
                    rows={2}
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground resize-none focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1.5 block">
                    <CalendarIcon size={12} className="inline me-1" />{t("settings.closeDateAndTime")}
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button className="w-full flex items-center justify-between border border-border rounded-lg px-4 py-2.5 text-sm text-foreground hover:border-primary/30 transition-colors">
                        {deadline ? format(deadline, "dd MMM yyyy") : t("settings.pickDate")}
                        <CalendarIcon size={16} className="text-muted-foreground" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={deadline} onSelect={setDeadline} disabled={(date) => date < new Date()} initialFocus className={cn("p-3 pointer-events-auto")} />
                    </PopoverContent>
                  </Popover>
                  <div className="flex gap-2 mt-2">
                    <select value={deadlineTime} onChange={(e) => setDeadlineTime(e.target.value)} className="flex-1 border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground">
                      {Array.from({ length: 12 }, (_, i) => { const h = String(i + 1).padStart(2, "0"); return <option key={h} value={`${h}:00`}>{h}:00</option>; })}
                    </select>
                    <select value={deadlinePeriod} onChange={(e) => setDeadlinePeriod(e.target.value as "AM" | "PM")} className="border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground">
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                  <select value={timezone} onChange={(e) => setTimezone(e.target.value)} className="w-full mt-2 border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground">
                    <option value="Asia/Dubai">Gulf Standard Time (UTC+4)</option>
                    <option value="America/New_York">Eastern Time (UTC-5)</option>
                    <option value="America/Denver">Mountain Time (UTC-7)</option>
                    <option value="America/Los_Angeles">Pacific Time (UTC-8)</option>
                    <option value="Europe/Paris">Central European Time (UTC+1)</option>
                    <option value="Europe/London">Greenwich Mean Time (UTC+0)</option>
                  </select>
                  <p className="text-xs text-muted-foreground mt-1.5">{t("settings.contributionsClose")}</p>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm text-foreground">{t("settings.autoReminders")}</span>
                    <HelpCircle size={13} className="text-muted-foreground" />
                  </div>
                  <Switch checked={autoReminders} onCheckedChange={setAutoReminders} />
                </div>
              </div>
            )}
          </div>
        );

      case "rsvp":
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground">{t("settings.enableRsvp")}</span>
              <Switch checked={rsvpEnabled} onCheckedChange={setRsvpEnabled} />
            </div>
            {rsvpEnabled && (
              <div className="space-y-3 ps-1">
                <div>
                  <Label className="text-xs text-muted-foreground mb-1.5 block"><MapPin size={12} className="inline me-1" />{t("settings.venueName")}</Label>
                  <Input value={rsvpLocationName} onChange={(e) => setRsvpLocationName(e.target.value)} placeholder={t("settings.venueNamePlaceholder")} className="h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1.5 block">{t("settings.address")}</Label>
                  <Input value={rsvpLocationAddress} onChange={(e) => setRsvpLocationAddress(e.target.value)} placeholder={t("settings.addressPlaceholder")} className="h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1.5 block"><CalendarIcon size={12} className="inline me-1" />{t("settings.eventDateTime")}</Label>
                  <div className="flex gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className={cn("flex-1 justify-start text-start text-sm h-9", !rsvpEventDate && "text-muted-foreground")}>
                          {rsvpEventDate ? format(rsvpEventDate, "dd MMM yyyy") : t("settings.pickDateShort")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={rsvpEventDate} onSelect={setRsvpEventDate} disabled={(date) => date < new Date()} initialFocus className={cn("p-3 pointer-events-auto")} />
                      </PopoverContent>
                    </Popover>
                    <Input type="time" value={rsvpEventTime} onChange={(e) => setRsvpEventTime(e.target.value)} className="w-28 h-9 text-sm" />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <UserPlus size={14} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">{t("settings.allowPlusOne")}</span>
                  </div>
                  <Switch checked={rsvpPlusOne} onCheckedChange={setRsvpPlusOne} />
                </div>

                {/* Capacity limit */}
                <div className="border-t border-border pt-3 space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1.5 block">
                      <Users size={12} className="inline me-1" />
                      {t("settings.maxCapacity", { defaultValue: "Maximum capacity" })}
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      value={maxCapacity}
                      onChange={(e) => setMaxCapacity(e.target.value)}
                      placeholder={t("settings.maxCapacityPlaceholder", { defaultValue: "Unlimited" })}
                      className="h-9 text-sm"
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {t("settings.maxCapacityDesc", { defaultValue: "Leave empty for unlimited. A waitlist opens when capacity is reached." })}
                    </p>
                  </div>
                </div>

                {/* Invite-only */}
                <div className="border-t border-border pt-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm text-foreground">{t("settings.inviteOnly", { defaultValue: "Invite-only event" })}</span>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {t("settings.inviteOnlyDesc", { defaultValue: "Only whitelisted emails can view event details. Manage guests via Invite People." })}
                      </p>
                    </div>
                    <Switch checked={inviteOnly} onCheckedChange={setInviteOnly} />
                  </div>
                </div>

                <div className="border-t border-border pt-3">
                  <RsvpQuestionBuilder questions={rsvpQuestions} onChange={setRsvpQuestions} />
                </div>
              </div>
            )}
          </div>
        );

      case "danger":
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{t("settings.permanentActions")}</p>
            {confirmDelete ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-destructive">{t("settings.deleteConfirm")}</p>
                <div className="flex gap-2">
                  <Button onClick={handleDelete} disabled={deleting} variant="destructive" className="flex-1 rounded-full">
                    {deleting ? t("settings.deleting") : t("settings.confirmDelete")}
                  </Button>
                  <Button onClick={() => setConfirmDelete(false)} variant="outline" className="flex-1 rounded-full">
                    {t("common.cancel")}
                  </Button>
                </div>
              </div>
            ) : (
              <Button onClick={handleDelete} disabled={deleting} variant="outline" className="w-full rounded-full text-destructive border-destructive/30 hover:bg-destructive/5">
                {t("settings.deleteThisCard")}
              </Button>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-background rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl relative"
      >
        {/* Header */}
        <div className="flex items-center py-4 border-b border-border sticky top-0 bg-background rounded-t-2xl z-10 px-4">
          {activeSection !== "home" ? (
            <button
              onClick={() => setActiveSection("home")}
              className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
          ) : (
            <div className="w-8" />
          )}
          <h2 className="text-lg font-bold text-foreground font-serif flex-1 text-center">
            {activeSection === "home" ? t("settings.title") : sections.find(s => s.key === activeSection)?.label}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          {activeSection === "home" ? (
            <div className="grid grid-cols-3 gap-3">
              {sections.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setActiveSection(s.key)}
                  className={cn(
                    "flex flex-col items-center gap-2 p-4 rounded-xl border border-border hover:border-primary/30 hover:bg-primary/5 transition-all group",
                    s.key === "danger" && "border-destructive/20 hover:border-destructive/40 hover:bg-destructive/5"
                  )}
                >
                  <div className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center transition-colors",
                    s.key === "danger"
                      ? "bg-destructive/10 text-destructive group-hover:bg-destructive/20"
                      : "bg-primary/10 text-primary group-hover:bg-primary/20"
                  )}>
                    <s.icon size={20} />
                  </div>
                  <div className="text-center">
                    <p className={cn(
                      "text-xs font-semibold",
                      s.key === "danger" ? "text-destructive" : "text-foreground"
                    )}>{s.label}</p>
                    <p className="text-[10px] text-muted-foreground leading-tight mt-0.5">{s.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="min-h-[200px]">
              {renderSectionContent()}
            </div>
          )}
        </div>

        {/* Sticky footer save button — only on sub-pages (not danger) */}
        {activeSection !== "home" && activeSection !== "danger" && activeSection !== "gifts" && (
          <div className="sticky bottom-0 bg-background border-t border-border px-5 py-4">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
              size="lg"
            >
              {saving ? t("settings.saving") : t("settings.saveChanges")}
            </Button>
          </div>
        )}
      </motion.div>

      {customizerOpen && (
        <Suspense fallback={null}>
          <FabricEditor
            mode="user"
            initialJson={customizerJson}
            onClose={() => setCustomizerOpen(false)}
            onUserSave={handleCustomizerSave}
          />
        </Suspense>
      )}
    </motion.div>
  );
};

export default SettingsModal;
